import { Component, OnInit, ElementRef, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { QuestionnaireQuestionTypeModalComponent } from '../question-type-modal/question-type-modal.component';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { ApiRequestsService } from '@base/api/api.gateway';
import { HelpersService, HeaderService, NavigationService } from '@base/shared/services';
import { EyrusWarningModalComponent } from '@base/shared/components';
import { omit, camelCase } from 'lodash';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { filter, map, mergeMap } from 'rxjs/operators';
import { ProjectsService } from '@eyrus/shared/services';
import { QuestionnaireService } from '../questionnaire.service';
import { MatButton } from '@angular/material/button';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'eyrus-questionnaire-creation-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.sass'],
  entryComponents: [
    QuestionnaireQuestionTypeModalComponent,
    EyrusWarningModalComponent
  ]
})
export class QuestionnaireFormComponent implements OnInit {

  @ViewChild('addQuestion0') addbuttonTop: MatButton;
  @ViewChild('addQuestion1') addbuttonBottom: MatButton;

  questionList: Array<any> = [];
  questionsForms: FormGroup;
  loading: Boolean = false;
  contractId: String = '';
  questionnaireType: String = 'generalQuestionnaire';

  private questionTypes: Array<any> = [];
  private viewType: String = 'general';
  private subscriptions: any = {};

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private modalService: NgbModal,
    private fb: FormBuilder,
    private api: ApiRequestsService,
    private helpers: HelpersService,
    private header: HeaderService,
    private projects: ProjectsService,
    private navigation: NavigationService,
    private questionnaireService: QuestionnaireService,
    private translate: TranslateService
  ) { }

  ngOnInit() {
    this.contractId = this.activatedRoute.snapshot.params.rootContractId;
    this.viewType = this.activatedRoute.snapshot.data.viewType;
    this.getQuestionsTypes();
    this.bootStrapForm();
  }

  ngOnDestroy() {
    this.header.setIcon({});
  }

  /**
   * Open Modal to select a question type
   */
  openQuestionAddModal() {
    const instance = this.modalService.open(QuestionnaireQuestionTypeModalComponent);
    instance.componentInstance.questionTypes = this.questionTypes;

    instance.result
      .then(res => {
        // this.addbuttonBottom._elementRef.nativeElement.blur();
        // this.addbuttonBottom._elementRef.nativeElement.blur();
        this.handleSelection(res);
      })
      .catch(err => {
        setTimeout(() => {
          // this.addbuttonBottom._elementRef.nativeElement.blur();
          // this.addbuttonBottom._elementRef.nativeElement.blur();
        }, 100);
      });
  }

  /**
   * Submit form update
   * @param param0 QuestionnaireForm
   */
  onSubmit({ value, valid }: { value: any, valid: boolean }) {

    // this.loading = true;

    const payload = this.questionnaireService.makeRequestPayload(value.questions);
    let type = this.viewType === 'general' ? 'general_questionnaire' : 'safety_questionnaire';

    const _actionTyoe = this.activatedRoute.snapshot.data.contentType;
    this.questionnaireType = camelCase(type);

    if (_actionTyoe === 'walkthrough') {
      type = 'questionnaire';
      this.questionnaireType = 'walkthrough';
    }

    const _label = 'common.successfullyUpdated';
    const _title = this.translate.instant(`questionnaire.${this.questionnaireType}`);

    const _msg = this.translate.instant(_label, { section: _title });

    const action = _actionTyoe === 'walkthrough' ? this.api.contracts.storeQuestionnairWalkeQuestions(this.contractId, type, payload) :
      this.api.contracts.storeQuestionnaireQuestions(this.contractId, type, payload);

    action
      .toPromise()
      .then(res => {
        this.loading = false;

        this.helpers.successMessage('', _msg);

        this.navigation.navigate({
          stepsBack: 3,
          type: 'basic'
        });

      })
      .catch(err => {
        this.helpers.errorMessage(err);
        this.loading = false;
      });
  }

  /**
   * Remove question by index
   * @param index question position
   */
  removeQuestion(index) {
    const form = this.questionsForms.get('questions') as FormArray;
    form.removeAt(index);
    this.questionList.splice(index, 1);

    if (!this.questionList.length)
      this.header.setIcon({});
  }

  /**
   * Remove an option from a question
   * @param questionIndex index of question form
   * @param optionIndex index of option to be deleted
   */
  removeOption(questionIndex, optionIndex) {
    const mainForm = this.questionsForms.get('questions') as FormArray;
    const mainQuestion = mainForm.at(questionIndex);
    const optionsForm = mainQuestion.get('answerOptions') as FormArray;

    if (!optionsForm)
      return;

    optionsForm.removeAt(optionIndex);

    if (optionIndex === 0 && !optionsForm.controls.length)
      this.addOption(questionIndex);
  }

  /**
   * Add option to a question
   * @param questionIndex index of question to be added an option
   */
  addOption(questionIndex) {
    const mainForm = this.questionsForms.get('questions') as FormArray;
    const mainQuestion = mainForm.at(questionIndex);

    const optionsForm = mainQuestion.get('answerOptions') as FormArray;

    if (!optionsForm)
      return;

    const _options = this.fb.group({
      availableAnswerText: [
        { value: '', disabled: false },
        [Validators.required],
      ]
    });

    optionsForm.push(_options);
  }

  /**
   * Preview questionnaire
   */
  preview() {
    const value = this.questionsForms.value;
    this.questionnaireService.questionnaire = this.questionnaireService.setQuestionnairePayload(value.questions);

    this.router.navigate([`preview`], { relativeTo: this.activatedRoute });
  }

  goBack() {
    this.navigation.navigate({
      type: 'basic',
      stepsBack: 3
    });
  }

  /**
   * Create question field options
   * @param option selection from modal
   */
  private handleSelection(option: any) {
    const questionType = option.questionTypeName.toLowerCase();
    const options = {
      questionText: [
        { value: option.questionText || '', disabled: false },
        [Validators.required],
      ],
      required: [
        { value: option.isRequired || false, disabled: false }
      ],
      questionTypeConst: [
        { value: option.questionTypeConst, disabled: false }
      ],
      questionTypeId: [
        { value: option.questionTypeId, disabled: false }
      ],
      questionType: [
        { value: questionType, disabled: false }
      ]
    };

    switch (questionType) {
      // case 'yes/no': 
      case 'dropdown':
        let form: any = [];

        if (option.answerOptions) {
          const _answers = option.answerOptions.map(answer => {

            const _item = this.fb.group({
              availableAnswerText: [
                { value: answer.availableAnswerText || '', disabled: false },
                [Validators.required],
              ]
            });

            return _item;
          });
          form = this.fb.array(_answers);
        } else {
          const fbGroup = this.fb.group({
            availableAnswerText: [
              { value: '', disabled: false },
              [Validators.required],
            ]
          });

          form = this.fb.array([]);
          form.push(fbGroup);
        }

        options['answerOptions'] = form;
        break;

      default:
        break;
    }

    this.addItemToForm(options);
  }

  /**
   * Push items to questions form
   */
  private addItemToForm(options) {
    const form = this.questionsForms.get('questions') as FormArray;
    const _options = this.fb.group(options);
    form.push(_options);

    if (!this.questionList.length)
      this.header.setIcon({
        icon: 'ae-recycle-bin',
        action: () => this.deleteAllConfirm()
      });

    this.questionList.push(_options);
  }

  /**
   * Initialize form
   */
  private bootStrapForm() {
    this.questionsForms = this.fb.group({
      questions: this.fb.array([])
    });
  }

  /**
   * Make request to get question types items
  */
  private getQuestionsTypes() {
    this.api.questionnaire.listQuestionTypes().toPromise()
      .then((res) => {
        this.questionTypes = res.data || [];
        this.listQuestions();
      })
      .catch(err => {
        this.helpers.errorMessage(err);
      });
  }

  /**
   * Open modal to confirm remove of all questions
   */
  private deleteAllConfirm() {
    const instance = this.modalService.open(EyrusWarningModalComponent);

    instance.componentInstance.title = '';
    instance.componentInstance.message = 'Are you sure you want to delete all questions?';

    instance.result
      .then(() => {
        this.questionList.length = 0;
        this.questionsForms = this.fb.group({
          questions: this.fb.array([])
        });
        this.header.setIcon({});
      })
      .catch(err => { });
  }

  /**
   * List settings questions
   */
  private listQuestions() {

    const contractDefinitionId = this.projects.projectId;
    const sectionId = this.projects.projectId;
    // getListContractDailyStatusSafety

    let type = this.viewType === 'general' ? 'general_questionnaire' : 'safety_questionnaire';

    const _actionTyoe = this.activatedRoute.snapshot.data.contentType;
    this.questionnaireType = camelCase(type);

    if (_actionTyoe === 'walkthrough') {
      type = 'questionnaire';
      this.questionnaireType = 'walkthrough';
    }

    const action = _actionTyoe === 'walkthrough' ? this.api.contracts.getWalkthroughQuestionnaireSettings(this.contractId, type) :
      this.api.contracts.getQuestionnaireSettings(this.contractId, type);

    action
      .toPromise()
      .then(res => {
        const _data = res.data || [];

        _data.forEach(question => {
          const options = question;
          const vals: any = this.getQuestionTypeName(question.questionTypeConst);
          options.questionTypeName = vals.questionTypeName;
          options.questionTypeId = vals.questionTypeId;
          this.handleSelection(question);
        });
      })
      .catch(err => this.helpers.errorMessage(err));

    // this.api.questionnaire.listSettingsQuestionsAnswers(this.viewType, sectionId, contractDefinitionId)
    //   .toPromise()
    //   .then(res => {
    //     res.data = res.data || [];
    //     res.data.forEach(question => {
    //       const options = question;
    //       options.questionTypeName = this.getQuestionTypeName(question.questionTypeId);
    //       this.handleSelection(question);
    //     });
    //   })
    //   .catch(err => this.helpers.errorMessage(err));
  }

  /**
   * Get question type from list
   * @param typeId type id
   */
  private getQuestionTypeName(typeId) {

    let _type = typeId.toLowerCase();

    if (_type === 'yesno')
      _type = 'yes/no';

    const type = this.questionTypes.find((e) => e.questionTypeName.toLowerCase() === _type);

    if (!type)
      return {
        questionTypeName: 'text',
        questionTypeId: 2,
      };

    return {
      questionTypeName: type.questionTypeName,
      questionTypeId: type.questionTypeId
    };
  }

}
